import {
  incompleteClaimFields,
  withIncompleteClaimValidation,
} from './incompleteClaim';
import { z } from 'zod';
import { baseClaimSchema } from './baseClaim';
import { basePatientSchema } from './basePatient';
import { providerSchema } from './provider';
import { tariffSchema, tariffSchemaWithRequiredICD9 } from './tariff';
import { healthcardSchema } from './healthcard';
import { siteSchema } from './site';
import { validateClaimLocationOfService } from './claimLocationOfService';
import { payorSchema } from './payor';
import {
  requiresHospitalDetails,
  validateProvinceClaimFields,
} from './provinceClaimFields';

const individualBulkClaimBaseSchema = baseClaimSchema
  .omit({
    id: true,
    claimNumber: true,
    updatedAt: true,
  })
  .extend({
    internalComments: z.string().optional().nullable(),
    serviceStart: z.string(),
    createdAt: z.string(),
    serviceEnd: z.string(),
    // Using a partial version of basePatientSchema to allow flexibility in patient data validation.
    patient: z.lazy(() => basePatientSchema.partial()),
    provider: z.lazy(() => providerSchema),
    tariffs: z.array(z.lazy(() => tariffSchema)),
    healthcard: z.lazy(() => healthcardSchema.optional().nullable()),
    site: z.lazy(() => siteSchema),
    payor: z
      .lazy(() => payorSchema)
      .optional()
      .nullable(),
    patientID: z.uuid().or(z.literal('')).optional().nullable(),
    healthcardID: z.uuid().or(z.literal('')).optional().nullable(),
    isNewPatient: z.boolean().optional(),
  });
export const individualBulkClaimSchema = individualBulkClaimBaseSchema
  .superRefine((data, ctx) => {
    const requiresTariffIcd9 = data.site?.requiresTariffIcd9 ?? true;
    if (requiresTariffIcd9) {
      data.tariffs.forEach((tariff, index) => {
        const parsedTariff = tariffSchemaWithRequiredICD9.safeParse(tariff);
        if (parsedTariff.success) {
          return;
        }

        parsedTariff.error.issues
          .filter((issue) => issue.path[0] === 'icd9')
          .forEach((issue) => {
            ctx.addIssue({
              code: 'custom',
              message: issue.message,
              path: ['tariffs', index, ...issue.path],
            });
          });
      });
    }

    // If serviceLocationIndicator is set (not null/undefined and not blank '   ' or ' '),
    // then hospitalCode and hospitalName must both be populated (hospitalCode not 3 spaces)
    const hasServiceLocationIndicator = requiresHospitalDetails(data);

    if (hasServiceLocationIndicator) {
      const hospitalCode = data.hospitalCode;
      const hospitalName = data.hospitalName;
      const hospitalCodeIsBlank = !hospitalCode || /^\s{3}$/.test(hospitalCode);
      const hospitalNameIsBlank = !hospitalName || /^\s{3}$/.test(hospitalName);

      // If only one of hospitalCode or hospitalName is blank and not the other,
      // add a distinct issue for the blank one
      if (hospitalCodeIsBlank && !hospitalNameIsBlank) {
        ctx.addIssue({
          code: 'custom',
          message:
            'Hospital Code is required when Service Location Indicator is set',
          path: ['hospitalCode'],
        });
      }
      if (hospitalNameIsBlank && !hospitalCodeIsBlank) {
        ctx.addIssue({
          code: 'custom',
          message:
            'Hospital Name is required when Service Location Indicator is set',
          path: ['hospitalName'],
        });
      }

      // If either hospitalCode or hospitalName is blank, add a general issue for both
      if (hospitalCodeIsBlank || hospitalNameIsBlank) {
        ctx.addIssue({
          code: 'custom',
          message:
            'Hospital Name is required when Service Location Indicator is set',
          path: ['hospitalName'],
        });
      }
    }
  })
  .superRefine(validateClaimLocationOfService)
  .superRefine(validateProvinceClaimFields);

export type IndividualBulkClaimType = z.infer<typeof individualBulkClaimSchema>;

export const individualBulkClaimSaveSchema = withIncompleteClaimValidation(
  individualBulkClaimSchema,
  individualBulkClaimBaseSchema.extend(incompleteClaimFields)
);
